import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Users, Euro, ShoppingBag, TrendingUp, Search, Filter,
  ChevronDown, ChevronUp, Check, Clock, CreditCard, Mail,
  ExternalLink, Loader2, AlertCircle
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLAN_BADGES = {
  starter: { label: 'Ambassador', color: 'bg-emerald-100 text-emerald-800' },
  pro: { label: 'Influencer', color: 'bg-blue-100 text-blue-800' },
  premium: { label: 'Elite', color: 'bg-amber-100 text-amber-800' }
};

const COMMISSION_STATUS = {
  pending: { label: 'In attesa', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  approved: { label: 'Approvata', color: 'bg-blue-100 text-blue-800', icon: Check },
  paid: { label: 'Pagata', color: 'bg-green-100 text-green-800', icon: CreditCard }
};

const AdminAffiliates = () => {
  const [affiliates, setAffiliates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedAffiliate, setSelectedAffiliate] = useState(null);
  const [commissions, setCommissions] = useState([]);
  const [loadingCommissions, setLoadingCommissions] = useState(false);
  const [stats, setStats] = useState({ total: 0, totalCommissions: 0, pendingCommissions: 0 });

  useEffect(() => {
    fetchAffiliates();
  }, []);

  const fetchAffiliates = async () => {
    try {
      const token = localStorage.getItem('qantri_admin_token');
      const response = await axios.get(`${API}/admin/affiliates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAffiliates(response.data.affiliates);
      
      // Calculate stats
      const totalCommissions = response.data.affiliates.reduce(
        (sum, a) => sum + (a.stats?.total_commission || 0), 0
      );
      const pendingCommissions = response.data.affiliates.reduce(
        (sum, a) => sum + (a.stats?.pending_commission || 0), 0
      );
      setStats({
        total: response.data.total,
        totalCommissions,
        pendingCommissions
      });
    } catch (error) {
      toast.error('Errore nel caricamento affiliati');
    } finally {
      setLoading(false);
    }
  };

  const fetchCommissions = async (affiliateId) => {
    setLoadingCommissions(true);
    try {
      const token = localStorage.getItem('qantri_admin_token');
      const response = await axios.get(`${API}/admin/affiliates/${affiliateId}/commissions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCommissions(response.data.commissions);
    } catch (error) {
      toast.error('Errore nel caricamento commissioni');
    } finally {
      setLoadingCommissions(false);
    }
  };

  const updateCommissionStatus = async (affiliateId, orderId, newStatus) => {
    try {
      const token = localStorage.getItem('qantri_admin_token');
      await axios.put(
        `${API}/admin/affiliates/${affiliateId}/commission-status?order_id=${orderId}&status=${newStatus}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Commissione aggiornata a "${COMMISSION_STATUS[newStatus].label}"`);
      fetchCommissions(affiliateId);
      fetchAffiliates();
    } catch (error) {
      toast.error('Errore nell\'aggiornamento');
    }
  };

  const selectAffiliate = (affiliate) => {
    if (selectedAffiliate?.id === affiliate.id) {
      setSelectedAffiliate(null);
      setCommissions([]);
    } else {
      setSelectedAffiliate(affiliate);
      fetchCommissions(affiliate.id);
    }
  };

  const filteredAffiliates = affiliates.filter(a =>
    a.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    a.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    a.referral_code?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div data-testid="admin-affiliates">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold mb-2">Gestione Affiliati</h1>
        <p className="text-muted-foreground">Monitora e gestisci i tuoi partner</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl p-5 shadow-sm border"
        >
          <div className="flex items-center justify-between mb-3">
            <Users className="h-5 w-5 text-primary" />
          </div>
          <p className="text-3xl font-bold">{stats.total}</p>
          <p className="text-sm text-muted-foreground">Affiliati totali</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-xl p-5 shadow-sm border"
        >
          <div className="flex items-center justify-between mb-3">
            <ShoppingBag className="h-5 w-5 text-blue-500" />
          </div>
          <p className="text-3xl font-bold">
            {affiliates.reduce((sum, a) => sum + (a.stats?.total_orders || 0), 0)}
          </p>
          <p className="text-sm text-muted-foreground">Ordini generati</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-xl p-5 shadow-sm border"
        >
          <div className="flex items-center justify-between mb-3">
            <Euro className="h-5 w-5 text-emerald-500" />
          </div>
          <p className="text-3xl font-bold text-emerald-600">€{stats.totalCommissions.toFixed(2)}</p>
          <p className="text-sm text-muted-foreground">Commissioni totali</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-xl p-5 shadow-sm border"
        >
          <div className="flex items-center justify-between mb-3">
            <Clock className="h-5 w-5 text-amber-500" />
          </div>
          <p className="text-3xl font-bold text-amber-600">€{stats.pendingCommissions.toFixed(2)}</p>
          <p className="text-sm text-muted-foreground">Da pagare</p>
        </motion.div>
      </div>

      {/* Search */}
      <div className="bg-white rounded-xl shadow-sm border p-4 mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Cerca per nome, email o codice referral..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {/* Affiliates List */}
      <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
        <div className="grid grid-cols-12 gap-4 p-4 bg-muted/50 text-sm font-medium text-muted-foreground">
          <div className="col-span-3">Affiliato</div>
          <div className="col-span-2">Piano</div>
          <div className="col-span-2">Codice</div>
          <div className="col-span-2 text-right">Ordini</div>
          <div className="col-span-2 text-right">Commissioni</div>
          <div className="col-span-1"></div>
        </div>

        {filteredAffiliates.length === 0 ? (
          <div className="p-12 text-center">
            <Users className="h-12 w-12 text-muted-foreground/30 mx-auto mb-4" />
            <p className="text-muted-foreground">Nessun affiliato trovato</p>
          </div>
        ) : (
          <div className="divide-y">
            {filteredAffiliates.map((affiliate) => {
              const plan = PLAN_BADGES[affiliate.partner_plan] || PLAN_BADGES.starter;
              const isExpanded = selectedAffiliate?.id === affiliate.id;

              return (
                <div key={affiliate.id}>
                  <div
                    className={`grid grid-cols-12 gap-4 p-4 items-center hover:bg-muted/30 cursor-pointer transition-colors ${
                      isExpanded ? 'bg-muted/50' : ''
                    }`}
                    onClick={() => selectAffiliate(affiliate)}
                  >
                    <div className="col-span-3">
                      <p className="font-medium truncate">{affiliate.name}</p>
                      <p className="text-sm text-muted-foreground truncate">{affiliate.email}</p>
                    </div>
                    <div className="col-span-2">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${plan.color}`}>
                        {plan.label}
                      </span>
                      <span className="ml-2 text-xs text-muted-foreground">
                        {affiliate.partner_commission || 5}%
                      </span>
                    </div>
                    <div className="col-span-2">
                      <code className="bg-muted px-2 py-1 rounded text-sm font-mono">
                        {affiliate.referral_code}
                      </code>
                    </div>
                    <div className="col-span-2 text-right">
                      <span className="font-medium">{affiliate.stats?.total_orders || 0}</span>
                      <span className="text-muted-foreground text-sm ml-1">ordini</span>
                    </div>
                    <div className="col-span-2 text-right">
                      <p className="font-medium text-emerald-600">
                        €{(affiliate.stats?.total_commission || 0).toFixed(2)}
                      </p>
                      {affiliate.stats?.pending_commission > 0 && (
                        <p className="text-xs text-amber-600">
                          +€{affiliate.stats.pending_commission.toFixed(2)} in attesa
                        </p>
                      )}
                    </div>
                    <div className="col-span-1 text-right">
                      {isExpanded ? (
                        <ChevronUp className="h-5 w-5 text-muted-foreground" />
                      ) : (
                        <ChevronDown className="h-5 w-5 text-muted-foreground" />
                      )}
                    </div>
                  </div>

                  {/* Expanded Commission Details */}
                  {isExpanded && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      className="bg-muted/20 border-t"
                    >
                      <div className="p-4">
                        <div className="flex items-center justify-between mb-4">
                          <h4 className="font-medium">Storico Commissioni</h4>
                          <a
                            href={`mailto:${affiliate.email}`}
                            className="text-sm text-primary hover:underline flex items-center gap-1"
                          >
                            <Mail className="h-4 w-4" />
                            Contatta
                          </a>
                        </div>

                        {loadingCommissions ? (
                          <div className="flex items-center justify-center py-8">
                            <Loader2 className="h-6 w-6 animate-spin text-primary" />
                          </div>
                        ) : commissions.length === 0 ? (
                          <div className="text-center py-8 text-muted-foreground">
                            <AlertCircle className="h-8 w-8 mx-auto mb-2 opacity-50" />
                            <p>Nessuna commissione ancora</p>
                          </div>
                        ) : (
                          <div className="space-y-2">
                            {commissions.map((commission) => {
                              const status = COMMISSION_STATUS[commission.commission_status] || COMMISSION_STATUS.pending;
                              const StatusIcon = status.icon;

                              return (
                                <div
                                  key={commission.order_id}
                                  className="bg-white rounded-lg p-4 flex items-center justify-between"
                                >
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-1">
                                      <span className="font-mono text-sm">
                                        #{commission.order_id.slice(-8)}
                                      </span>
                                      <span className={`px-2 py-0.5 rounded-full text-xs ${status.color}`}>
                                        <StatusIcon className="h-3 w-3 inline mr-1" />
                                        {status.label}
                                      </span>
                                    </div>
                                    <p className="text-sm text-muted-foreground">
                                      {new Date(commission.date).toLocaleDateString('it-IT')} •{' '}
                                      Ordine €{commission.order_total?.toFixed(2)}
                                    </p>
                                  </div>
                                  <div className="flex items-center gap-4">
                                    <div className="text-right">
                                      <p className="font-semibold text-emerald-600">
                                        +€{commission.commission_amount?.toFixed(2)}
                                      </p>
                                      <p className="text-xs text-muted-foreground">
                                        {commission.commission_percent}%
                                      </p>
                                    </div>
                                    {commission.commission_status !== 'paid' && (
                                      <div className="flex gap-1">
                                        {commission.commission_status === 'pending' && (
                                          <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              updateCommissionStatus(affiliate.id, commission.order_id, 'approved');
                                            }}
                                          >
                                            Approva
                                          </Button>
                                        )}
                                        {(commission.commission_status === 'pending' || commission.commission_status === 'approved') && (
                                          <Button
                                            size="sm"
                                            className="bg-emerald-600 hover:bg-emerald-700"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              updateCommissionStatus(affiliate.id, commission.order_id, 'paid');
                                            }}
                                          >
                                            Paga
                                          </Button>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </motion.div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminAffiliates;
